/**
 * Employee Edit Page — hardcoded form with key employee fields.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Input, Label,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract', 'intern', 'consultant'];
const EMPLOYMENT_STATUSES = ['active', 'on_leave', 'terminated', 'resigned', 'probation'];
const GENDERS = ['male', 'female', 'other'];

export function EmployeeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'employees', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();
  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => { if (data?.data) setForm({ ...data.data }); }, [data]);
  const set = (f: string, v: unknown) => setForm(p => p ? { ...p, [f]: v } : p);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, full_name, created_at, updated_at, created_by, ...values } = form;
    update({ resource: 'employees', id: id!, values }, { onSuccess: () => navigate(`/employees/${id}`) });
  }

  if (fetchLoading || !form) return (
    <div className="flex items-center justify-center py-12">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
    </div>
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/employees/${id}`)} disabled={updateLoading}>
          <ArrowLeft className="mr-2 h-4 w-4" />Back to Profile
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="first_name">First Name *</Label>
                <Input id="first_name" value={String(form.first_name ?? '')} onChange={e => set('first_name', e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="last_name">Last Name</Label>
                <Input id="last_name" value={String(form.last_name ?? '')} onChange={e => set('last_name', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={String(form.email ?? '')} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={String(form.phone ?? '')} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="employee_code">Employee Code</Label>
                <Input id="employee_code" value={String(form.employee_code ?? '')} onChange={e => set('employee_code', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input id="date_of_birth" type="date" value={form.date_of_birth ? String(form.date_of_birth).slice(0,10) : ''} onChange={e => set('date_of_birth', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="gender">Gender</Label>
                <select id="gender" className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm" value={String(form.gender ?? '')} onChange={e => set('gender', e.target.value)}>
                  <option value="">Select</option>
                  {GENDERS.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="blood_group">Blood Group</Label>
                <Input id="blood_group" value={String(form.blood_group ?? '')} onChange={e => set('blood_group', e.target.value)} placeholder="e.g. O+" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employment */}
        <Card>
          <CardHeader><CardTitle>Employment Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="date_of_joining">Date of Joining *</Label>
                <Input id="date_of_joining" type="date" value={form.date_of_joining ? String(form.date_of_joining).slice(0,10) : ''} onChange={e => set('date_of_joining', e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="date_of_confirmation">Confirmation Date</Label>
                <Input id="date_of_confirmation" type="date" value={form.date_of_confirmation ? String(form.date_of_confirmation).slice(0,10) : ''} onChange={e => set('date_of_confirmation', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="employment_type">Employment Type</Label>
                <select id="employment_type" className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm" value={String(form.employment_type ?? 'full_time')} onChange={e => set('employment_type', e.target.value)}>
                  {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="employment_status">Status</Label>
                <select id="employment_status" className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm" value={String(form.employment_status ?? 'active')} onChange={e => set('employment_status', e.target.value)}>
                  {EMPLOYMENT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader><CardTitle>Address</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="current_address">Current Address</Label>
              <Input id="current_address" value={String(form.current_address ?? '')} onChange={e => set('current_address', e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="city">City</Label>
                <Input id="city" value={String(form.city ?? '')} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="state">State</Label>
                <Input id="state" value={String(form.state ?? '')} onChange={e => set('state', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pincode">Pincode</Label>
                <Input id="pincode" value={String(form.pincode ?? '')} onChange={e => set('pincode', e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Government IDs */}
        <Card>
          <CardHeader><CardTitle>Government IDs</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="pan">PAN</Label>
                <Input id="pan" value={String(form.pan ?? '')} onChange={e => set('pan', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="aadhaar">Aadhaar</Label>
                <Input id="aadhaar" value={String(form.aadhaar ?? '')} onChange={e => set('aadhaar', e.target.value)} placeholder="12 digits" maxLength={12} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="uan">UAN</Label>
                <Input id="uan" value={String(form.uan ?? '')} onChange={e => set('uan', e.target.value)} maxLength={12} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pf_number">PF Number</Label>
                <Input id="pf_number" value={String(form.pf_number ?? '')} onChange={e => set('pf_number', e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(`/employees/${id}`)}>Cancel</Button>
          <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </form>
    </div>
  );
}

export default EmployeeEditPage;
